export function validQuantity(value) { return Number.isInteger(value) && value >= 1 && value <= 99; }

export function normalizeMetadata(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const sizes = source.sizes && typeof source.sizes === 'object' && !Array.isArray(source.sizes) ? structuredClone(source.sizes) : {};
  return {...structuredClone(source), searchCode: String(source.search_code || ''), sizes};
}

function variantSize(variant) { return String(variant?.option1 || variant?.title || '').trim(); }

export function selectVariant(variants, rawMetadata, requestedSize) {
  const size = String(requestedSize || '').trim();
  const variant = variants.find((candidate) => variantSize(candidate) === size);
  if (!variant) return null;
  const rawFit = normalizeMetadata(rawMetadata).sizes[size];
  const fit = rawFit && typeof rawFit === 'object' ? {
    neckCm: String(rawFit.neck_cm || ''), neckIn: String(rawFit.neck_in || ''),
    widthCm: String(rawFit.band_width_cm || ''), widthIn: String(rawFit.band_width_in || ''),
  } : null;
  return {id: variant.id, available: Boolean(variant.available), price: Number(variant.price), size, fit};
}

export function createPendingGuard() {
  let active = false;
  return {get pending() { return active; }, async run(operation) {
    if (active) return undefined;
    active = true;
    try { return await operation(); } finally { active = false; }
  }};
}

export function isExactFallbackSku(value) {
  return ['100567-SET-S', '100567-SET-M', '100567-SET-L'].includes(String(value || ''));
}

export function tableFitCells(fit) {
  const neck = fit?.neckCm ? `${fit.neckCm} cm${fit.neckIn ? ` (${fit.neckIn} in)` : ''}` : 'Not provided';
  const width = fit?.widthCm ? `${fit.widthCm} cm${fit.widthIn ? ` (${fit.widthIn} in)` : ''}` : 'Not provided';
  return {neck, width};
}

export function applyZoomSource(target, source) {
  target.removeAttribute('srcset');
  target.removeAttribute('sizes');
  target.src = source.currentSrc || source.src;
  target.alt = source.alt;
}

function money(cents, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency: currency || 'USD'}).format(cents / 100);
}

function appendText(parent, tag, value) {
  const node = document.createElement(tag);
  node.textContent = value;
  parent.append(node);
  return node;
}

function initialize(root) {
  if (root.dataset.msReady) return;
  root.dataset.msReady = 'true';
  const variants = JSON.parse(root.querySelector('[data-ms-variants]').textContent || '[]');
  const metadata = JSON.parse(root.querySelector('[data-ms-metadata]').textContent || '{}');
  const form = root.querySelector('[data-ms-form]');
  const price = root.querySelector('[data-ms-price]');
  const variantInput = form.querySelector('[name="id"]');
  const quantity = form.querySelector('[name="quantity"]');
  const add = root.querySelector('[data-ms-add]');
  const status = root.querySelector('[data-ms-status]');
  const bag = root.querySelector('[data-ms-view-bag]');
  const quickCheckout = form.querySelector('[data-pc-quick-checkout]');
  const fitBox = root.querySelector('[data-ms-fit]');
  const guard = createPendingGuard();
  let selected = null;

  function syncQuickCheckout() {
    if (!quickCheckout) return;
    quickCheckout.hidden = !selected?.available || !quantity.checkValidity();
    quickCheckout.inert = guard.pending;
    quickCheckout.setAttribute('aria-busy', String(guard.pending));
  }
  quantity.addEventListener('input', syncQuickCheckout);
  quantity.addEventListener('change', syncQuickCheckout);

  function syncFitTable() {
    root.querySelectorAll('[data-ms-size-row]').forEach((row) => {
      const record = selectVariant(variants, metadata, row.dataset.msSizeRow);
      const cells = tableFitCells(record?.fit);
      if (!record?.fit?.neckCm) row.cells[1].textContent = 'Not provided';
      else row.cells[1].textContent = cells.neck;
      if (row.cells[2]) row.cells[2].textContent = cells.width;
    });
  }

  function renderFit() {
    fitBox.replaceChildren();
    if (!selected?.fit) {
      appendText(fitBox, 'span', `SIZE ${selected?.size || ''} · FIT INFORMATION`);
      appendText(fitBox, 'strong', 'Not provided for this size');
      return;
    }
    appendText(fitBox, 'span', `SIZE ${selected.size} · FITS NECK`);
    const range = appendText(fitBox, 'strong', `${selected.fit.neckCm || 'Not provided'}${selected.fit.neckCm ? ' cm' : ''}`);
    if (selected.fit.neckIn) appendText(range, 'small', ` (${selected.fit.neckIn} in)`);
    if (selected.fit.widthCm || !metadata.hide_unknown_band_width) appendText(fitBox, 'p', `Band width: ${selected.fit.widthCm || 'Not provided'}${selected.fit.widthCm ? ' cm' : ''}${selected.fit.widthIn ? ` (${selected.fit.widthIn} in)` : ''}`);
    if (metadata.fit_note) appendText(fitBox, 'p', metadata.fit_note);
  }

  function render(size) {
    selected = selectVariant(variants, metadata, size);
    root.querySelectorAll('[data-ms-size]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.msSize === size)));
    root.querySelectorAll('[data-ms-size-row]').forEach((row) => row.setAttribute('aria-current', row.dataset.msSizeRow === size ? 'true' : 'false'));
    if (!selected) {
      variantInput.value = '';
      variantInput.dispatchEvent(new Event('input', {bubbles: true}));
      variantInput.dispatchEvent(new Event('change', {bubbles: true}));
      syncQuickCheckout();
      add.disabled = true;
      add.textContent = 'Size unavailable';
      renderFit();
      return;
    }
    if (variantInput.value !== String(selected.id)) {
      variantInput.value = String(selected.id);
      variantInput.dispatchEvent(new Event('input', {bubbles: true}));
      variantInput.dispatchEvent(new Event('change', {bubbles: true}));
    }
    syncQuickCheckout();
    price.textContent = `${money(selected.price, root.dataset.currency)} ${root.dataset.currency}`;
    add.disabled = guard.pending || !selected.available;
    add.textContent = guard.pending ? 'Adding…' : (selected.available ? 'Add the set to bag' : 'Sold out');
    renderFit();
  }

  root.querySelectorAll('[data-ms-size]').forEach((button) => button.addEventListener('click', () => render(button.dataset.msSize)));
  root.querySelectorAll('[data-ms-quantity]').forEach((button) => button.addEventListener('click', () => {
    quantity.value = String(Math.max(1, Math.min(99, (Number(quantity.value) || 1) + Number(button.dataset.msQuantity))));
    quantity.dispatchEvent(new Event('input', {bubbles: true}));
    quantity.dispatchEvent(new Event('change', {bubbles: true}));
  }));
  root.querySelectorAll('[data-ms-thumb]').forEach((button) => button.addEventListener('click', () => {
    const main = root.querySelector('[data-ms-main-image]');
    main.src = button.dataset.src;
    main.alt = button.dataset.alt;
    if (matchMedia('(max-width: 749px)').matches && root.classList.contains('dl-product-enhanced')) {
      main.closest('.main-image')?.scrollIntoView({block: 'start', behavior: 'instant'});
    }
    root.querySelectorAll('[data-ms-thumb]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
  }));

  function wireDialog(dialog, closeSelector) {
    let returnFocus = null;
    dialog.querySelector(closeSelector)?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => returnFocus?.focus());
    return {open(trigger) { returnFocus = trigger; dialog.showModal(); }};
  }

  const imageDialog = root.querySelector('[data-ms-image-dialog]');
  if (imageDialog) {
    const imageControl = wireDialog(imageDialog, '[data-ms-close-image]');
    root.querySelectorAll('[data-ms-main-zoom],[data-ms-macro-zoom]').forEach((button) => button.addEventListener('click', () => {
      const source = button.querySelector('img');
      const target = imageDialog.querySelector('img');
      applyZoomSource(target, source);
      imageControl.open(button);
    }));
  }

  const guideDialog = root.querySelector('[data-ms-guide-dialog]');
  const guideControl = wireDialog(guideDialog, '[data-ms-close-guide]');
  function activateGuide(name, focus = false) {
    root.querySelectorAll('[data-ms-guide-tab]').forEach((tab) => {
      const active = tab.dataset.msGuideTab === name;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      root.querySelector(`[data-ms-guide-panel="${tab.dataset.msGuideTab}"]`).hidden = !active;
      if (active && focus) tab.focus();
    });
  }
  root.querySelectorAll('[data-ms-open-guide]').forEach((button) => button.addEventListener('click', (event) => {
    event.preventDefault();
    activateGuide(button.dataset.msOpenGuide);
    guideControl.open(button);
    root.querySelector(`[data-ms-guide-tab="${button.dataset.msOpenGuide}"]`)?.focus();
  }));
  root.querySelectorAll('[data-ms-guide-tab]').forEach((tab) => {
    tab.addEventListener('click', () => activateGuide(tab.dataset.msGuideTab));
    tab.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const other = [...root.querySelectorAll('[data-ms-guide-tab]')].find((candidate) => candidate !== tab);
      activateGuide(other.dataset.msGuideTab, true);
    });
  });
  root.querySelector('[data-ms-open-materials]')?.addEventListener('click', (event) => {
    event.preventDefault();
    const details = root.querySelector('[data-ms-materials]');
    details.open = true;
    details.scrollIntoView({block: 'center'});
  });

  form.addEventListener('submit', async (event) => {
    // Leave accelerated checkout submissions to Shopify's native component.
    if (event.submitter && event.submitter !== add) return;
    if (!window.fetch) return;
    event.preventDefault();
    if (guard.pending) return;
    const requestedQuantity = Number(quantity.value);
    if (!selected?.available || !validQuantity(requestedQuantity) || !form.reportValidity()) {
      status.textContent = 'Choose an available size and a whole-number quantity from 1 to 99.';
      status.dataset.state = 'error';
      return;
    }
    await guard.run(async () => {
      render(selected.size);
      status.textContent = 'Adding…';
      bag.hidden = true;
      try {
        const endpoint = `${(form.dataset.addUrl || form.action).replace(window.location.origin, '').replace(/\.js$/, '')}.js`;
        const response = await fetch(endpoint, {method: 'POST', body: new FormData(form), headers: {Accept: 'application/json'}});
        const result = await response.json();
        if (!response.ok || result.status || !result.id) throw new Error(result.description || result.message || 'This set could not be added.');
        status.dataset.state = 'success';
        status.textContent = 'Added to your bag.';
        bag.hidden = false;
        document.dispatchEvent(new CustomEvent('cart:refresh', {detail: {item: result}}));
      } catch (error) {
        status.dataset.state = 'error';
        status.textContent = error.message === 'Failed to fetch' ? 'We could not reach the shop. Please try again.' : (error.message || 'This set could not be added. Please try again.');
      }
    });
    render(selected.size);
  });

  const initial = root.querySelector('[data-ms-size][aria-pressed="true"]') || root.querySelector('[data-ms-size]');
  syncFitTable();
  if (initial) render(initial.dataset.msSize);
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-diana-matching-set]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-diana-matching-set]').forEach(initialize));
}
